import { useEffect, useMemo, useState } from 'react';
import { Copy, Plus, Trash2, X } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useApiClient } from '../../api/apiClient';
import { useClient } from '../../context/ClientContext';
import { useMspApiKeys, useMspClients } from '../../hooks/useMspData';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import Skeleton from '../../components/ui/Skeleton';
import PageError from '../../components/ui/PageError';

const DAY_MS = 24 * 60 * 60 * 1000;

function clientRecordId(client) {
  return client?.clientId ?? client?.id ?? '';
}

function clientRecordName(client) {
  return client?.name ?? client?.companyName ?? client?.displayName ?? clientRecordId(client);
}

export function defaultRunnerKeyExpiry(now = new Date()) {
  return new Date(now.getTime() + (89 * DAY_MS)).toISOString().slice(0, 10);
}

export function buildRunnerKeyRequest({ name, clientId, expiresOn }) {
  const safeName = String(name || '').trim();
  const safeClientId = String(clientId || '').trim();
  if (!safeName) throw new Error('A key name is required.');
  if (!safeClientId) throw new Error('Select a client before creating the key.');
  if (!expiresOn) throw new Error('An expiration date is required.');

  const expiresAt = new Date(String(expiresOn) + 'T12:00:00.000Z');
  if (Number.isNaN(expiresAt.getTime())) throw new Error('Enter a valid expiration date.');

  return {
    name: safeName,
    scopes: ['ad.runner'],
    expiresAt: expiresAt.toISOString(),
    clientId: safeClientId,
    mspWide: false,
  };
}

export function normalizeRunnerKey(key, now = new Date()) {
  const scopes = Array.isArray(key?.scopes)
    ? key.scopes
    : String(key?.scopes || '').split(',').map(scope => scope.trim()).filter(Boolean);
  const expiresAt = key?.expiresAt ?? null;
  const expired = expiresAt ? new Date(expiresAt).getTime() <= now.getTime() : false;
  const isRevoked = Boolean(key?.isRevoked) || String(key?.status || '').toLowerCase() === 'revoked';

  return {
    id: key?.keyId ?? key?.id ?? '',
    clientId: key?.clientId ?? null,
    name: key?.name ?? 'Unnamed runner key',
    prefix: key?.keyPrefix ?? key?.prefix ?? '',
    scopes,
    createdAt: key?.createdAt ?? null,
    lastUsedAt: key?.lastUsedAt ?? null,
    expiresAt,
    status: isRevoked ? 'Revoked' : expired ? 'Expired' : 'Active',
  };
}

function formatDate(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fieldStyle() {
  return {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    borderRadius: 6,
    border: '1px solid ' + C.b1,
    background: C.s2,
    color: C.t1,
    fontFamily: FONTS.mono,
    fontSize: 11,
  };
}

export function ApiKeysPanel() {
  const api = useApiClient();
  const { clientId: activeClientId } = useClient();
  const { data: rawKeys, loading, error, refetch } = useMspApiKeys();
  const { data: clients, loading: clientsLoading, error: clientsError } = useMspClients();
  const [selectedClientId, setSelectedClientId] = useState(activeClientId || '');
  const [name, setName] = useState('Dedicated AD runner');
  const [expiresOn, setExpiresOn] = useState(() => defaultRunnerKeyExpiry());
  const [secret, setSecret] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revokingId, setRevokingId] = useState('');

  const normalizedKeys = useMemo(
    () => (Array.isArray(rawKeys) ? rawKeys : []).map(key => normalizeRunnerKey(key)),
    [rawKeys],
  );
  const activeCount = normalizedKeys.filter(key => key.status === 'Active').length;
  const clientList = Array.isArray(clients) ? clients : [];
  const clientNames = useMemo(
    () => new Map(clientList.map(client => [String(clientRecordId(client)), clientRecordName(client)])),
    [clientList],
  );

  useEffect(() => {
    if (clientList.length === 0) {
      setSelectedClientId('');
      return;
    }
    const available = new Set(clientList.map(client => String(clientRecordId(client))));
    if (selectedClientId && available.has(String(selectedClientId))) return;
    const preferred = activeClientId && available.has(String(activeClientId))
      ? String(activeClientId)
      : String(clientRecordId(clientList[0]));
    setSelectedClientId(preferred);
  }, [activeClientId, clientList, selectedClientId]);

  useEffect(() => () => setSecret(''), []);

  const handleCreate = async () => {
    setFeedback(null);
    setSecret('');
    let request;
    try {
      request = buildRunnerKeyRequest({ name, clientId: selectedClientId, expiresOn });
    } catch (validationError) {
      setFeedback({ ok: false, message: validationError.message });
      return;
    }

    setSaving(true);
    try {
      const created = await api.post('/msp/api-keys', request);
      if (!created?.key) throw new Error('The API did not return the one-time key.');
      setSecret(created.key);
      setFeedback({ ok: true, message: 'Client-scoped AD runner key created. Copy it now; it will not be shown again.' });
      await refetch();
    } catch (createError) {
      setFeedback({ ok: false, message: createError.message || 'Unable to create the API key.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setFeedback({ ok: true, message: 'One-time key copied to the clipboard.' });
    } catch {
      setFeedback({ ok: false, message: 'Clipboard access was unavailable. Select and copy the key manually.' });
    }
  };

  const handleRevoke = async key => {
    if (!window.confirm('Revoke ' + key.name + '? Finish any running AD job before revoking its key.')) return;
    setFeedback(null);
    setRevokingId(key.id);
    try {
      await api.delete('/msp/api-keys/' + encodeURIComponent(key.id));
      setSecret('');
      setFeedback({ ok: true, message: 'Runner key revoked.' });
      await refetch();
    } catch (revokeError) {
      setFeedback({ ok: false, message: revokeError.message || 'Unable to revoke the API key.' });
    } finally {
      setRevokingId('');
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 14 }}>
      <Card style={{ padding: 18, minWidth: 0 }}>
        <CardHeader
          title="AD Runner Keys"
          sub="Client-scoped credentials for dedicated Active Directory runners."
          action={<Tag label={activeCount + ' active'} color={activeCount ? C.grn : C.t3} />}
        />

        <div style={{ padding: '10px 12px', borderRadius: 7, border: '1px solid ' + C.sky + '33', background: C.sky + '10', color: C.t1, fontSize: 10, fontFamily: FONTS.mono, lineHeight: 1.6, marginBottom: 14 }}>
          Each key is restricted to one client and the ad.runner scope. Complete jobs claimed by an old key before rotating or revoking it.
        </div>

        {feedback && (
          <div role="status" style={{ padding: '9px 11px', borderRadius: 6, border: '1px solid ' + (feedback.ok ? C.grn : C.red) + '33', background: (feedback.ok ? C.grn : C.red) + '10', color: feedback.ok ? C.grn : C.red, fontSize: 10, fontFamily: FONTS.mono, marginBottom: 14 }}>
            {feedback.message}
          </div>
        )}

        {secret && (
          <div style={{ padding: '12px', borderRadius: 7, border: '1px solid ' + C.amb + '55', background: C.amb + '10', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 9, color: C.amb, textTransform: 'uppercase', fontFamily: FONTS.mono }}>One-time runner key</div>
              <button type="button" aria-label="Dismiss one-time key" onClick={() => setSecret('')} style={{ background: 'none', border: 'none', color: C.t2, cursor: 'pointer' }}><X size={14} /></button>
            </div>
            <div data-testid="one-time-runner-key" style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, wordBreak: 'break-all', userSelect: 'all', marginBottom: 8 }}>{secret}</div>
            <Button variant="sec" onClick={handleCopy}><Copy size={13} style={{ marginRight: 6 }} />Copy key</Button>
          </div>
        )}

        {(error || clientsError) && <PageError error={error || clientsError} />}
        {(loading || clientsLoading) && <Skeleton height={90} />}

        {!loading && normalizedKeys.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px 0', color: C.t2, fontSize: 11, fontFamily: FONTS.mono }}>
            No runner keys have been created for this MSP.
          </div>
        )}

        {!loading && normalizedKeys.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid ' + C.b1 }}>
                  {['Name', 'Client', 'Prefix', 'Scope', 'Last Used', 'Expires', 'Status', ''].map(header => (
                    <th key={header} style={{ textAlign: 'left', padding: '8px', color: C.t2, fontSize: 9, fontFamily: FONTS.mono, textTransform: 'uppercase' }}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {normalizedKeys.map(key => (
                  <tr key={key.id} style={{ borderBottom: '1px solid ' + C.b2, opacity: key.status === 'Revoked' ? 0.55 : 1 }}>
                    <td style={{ padding: 8, color: C.t1, fontSize: 11, fontWeight: 700 }}>{key.name}</td>
                    <td style={{ padding: 8, color: C.t2, fontSize: 10 }}>{clientNames.get(String(key.clientId)) || key.clientId || 'MSP-wide'}</td>
                    <td style={{ padding: 8, color: C.t2, fontSize: 10, fontFamily: FONTS.mono }}>{key.prefix ? key.prefix + '...' : '—'}</td>
                    <td style={{ padding: 8 }}><Tag label={key.scopes.join(', ') || '—'} color={C.sky} /></td>
                    <td style={{ padding: 8, color: C.t2, fontSize: 10, fontFamily: FONTS.mono }}>{formatDate(key.lastUsedAt)}</td>
                    <td style={{ padding: 8, color: C.t2, fontSize: 10, fontFamily: FONTS.mono }}>{formatDate(key.expiresAt)}</td>
                    <td style={{ padding: 8 }}><Tag label={key.status} color={key.status === 'Active' ? C.grn : key.status === 'Expired' ? C.amb : C.red} /></td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <button
                        type="button"
                        aria-label={'Revoke ' + key.name}
                        onClick={() => handleRevoke(key)}
                        disabled={key.status !== 'Active' || revokingId === key.id}
                        style={{ background: 'none', border: 'none', color: C.red, cursor: key.status === 'Active' ? 'pointer' : 'default', padding: 4 }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card style={{ padding: 18 }}>
        <CardHeader title="Create AD Runner Key" sub="The raw key is displayed once." />
        <label style={{ display: 'block', fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', marginBottom: 5 }}>Name</label>
        <input value={name} onChange={event => setName(event.target.value)} style={{ ...fieldStyle(), marginBottom: 10 }} />

        <label style={{ display: 'block', fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', marginBottom: 5 }}>Client</label>
        <select value={selectedClientId} onChange={event => setSelectedClientId(event.target.value)} disabled={clientsLoading} style={{ ...fieldStyle(), marginBottom: 10 }}>
          <option value="">Select a client</option>
          {clientList.map(client => {
            const id = String(clientRecordId(client));
            return <option key={id} value={id}>{clientRecordName(client)}</option>;
          })}
        </select>

        <label style={{ display: 'block', fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', marginBottom: 5 }}>Scope</label>
        <input value="ad.runner" readOnly aria-label="Scope" style={{ ...fieldStyle(), marginBottom: 10, opacity: 0.8 }} />

        <label style={{ display: 'block', fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', marginBottom: 5 }}>Expires</label>
        <input type="date" value={expiresOn} onChange={event => setExpiresOn(event.target.value)} style={{ ...fieldStyle(), marginBottom: 12 }} />

        <Button onClick={handleCreate} disabled={saving || !name.trim() || !selectedClientId || !expiresOn} style={{ width: '100%' }}>
          <Plus size={13} style={{ marginRight: 6 }} />{saving ? 'Creating...' : 'Create API Key'}
        </Button>
      </Card>
    </div>
  );
}

export default function ApiKeys() {
  return (
    <Shell title="API Keys" sub="Manage client-scoped runner credentials">
      <ApiKeysPanel />
    </Shell>
  );
}
